"use client";

import type React from "react";
import { useState, useEffect } from "react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { signIn } from "next-auth/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { useToast } from "@/shared/hooks/use-toast";
import { trackLogin, trackSignUp, trackEvent } from "@/shared/lib/analytics";
import { trackCompleteRegistration } from "@/shared/lib/meta-pixel-client";

type AuthStep = "email" | "login" | "signup" | "otp" | "forgot" | "twofa";

// Throws if the OTP send endpoint returns a non-2xx response, so the
// UI can surface a real error instead of silently advancing to the OTP
// step while no email is actually sent.
async function sendOtp(email: string): Promise<void> {
  const res = await fetch("/api/auth/send-email-otp", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) {
    let message = "Failed to send verification code. Please try again.";
    try {
      const data = await res.json();
      if (typeof data?.error === "string") message = data.error;
    } catch {
      // response was not JSON, keep default message
    }
    throw new Error(message);
  }
}

const Spinner = () => (
  <svg
    className="animate-spin -ml-1 mr-3 h-5 w-5"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

export default function LoginPage() {
  const [step, setStep] = useState<AuthStep>("email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  // Authenticator code, for accounts with a second factor enrolled
  const [totpCode, setTotpCode] = useState("");

  // OTP state
  const [otpValue, setOtpValue] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [otpError, setOtpError] = useState("");
  const [otpSending, setOtpSending] = useState(false);

  const router = useRouter();
  const searchParams = useSearchParams();
  const { toast } = useToast();

  // Handle URL params for messages
  useEffect(() => {
    const verified = searchParams.get("verified");
    const error = searchParams.get("error");
    const message = searchParams.get("message");

    if (verified === "true") {
      toast({
        title: "Email Verified",
        description: "Your email has been verified. You can now sign in.",
      });
    }
    if (error === "invalid_token") {
      toast({
        title: "Invalid Link",
        description: "The verification link is invalid or expired.",
        variant: "destructive",
      });
    }
    if (error === "OAuthAccountNotLinked") {
      toast({
        title: "Account already exists",
        description: "An account with this email already exists. Try signing in with your email and password instead.",
        variant: "destructive",
      });
    }
    if (error === "OAuthCallback" || error === "OAuthSignin") {
      toast({
        title: "Google sign-in failed",
        description: "Something went wrong connecting to Google. Please try again.",
        variant: "destructive",
      });
    }
    if (error === "admin_no_oauth") {
      toast({
        title: "Use your password",
        description:
          "Admin accounts sign in with email and password, then an authenticator code. Google sign-in isn't available for them.",
        variant: "destructive",
      });
    }
    if (error === "OAuthCreateAccount" || error === "Callback") {
      toast({
        title: "Sign-in error",
        description: "Could not complete sign-in. Please try again or use email instead.",
        variant: "destructive",
      });
    }
    if (error && !["invalid_token", "OAuthAccountNotLinked", "OAuthCallback", "OAuthSignin", "OAuthCreateAccount", "Callback", "admin_no_oauth"].includes(error)) {
      toast({
        title: "Sign-in error",
        description: `Something went wrong (${error}). Please try again.`,
        variant: "destructive",
      });
    }
    if (message === "already_verified") {
      toast({
        title: "Already Verified",
        description: "Your email is already verified. You can sign in.",
      });
    }
  }, [searchParams, toast]);

  // Cooldown timer for OTP resend
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  // Step 1: Check if email exists
  const handleEmailContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !email.includes("@")) {
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const res = await fetch("/api/auth/check-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      const data = await res.json();
      if (data.exists && !data.verified) {
        // User registered but never verified OTP — resend and go to OTP step
        try {
          await sendOtp(email.trim().toLowerCase());
        } catch (err) {
          toast({
            title: "Could not send code",
            description: err instanceof Error ? err.message : "Please try again.",
            variant: "destructive",
          });
          return;
        }
        setCooldown(60);
        setOtpValue("");
        setOtpError("");
        setStep("otp");
      } else {
        setStep(data.exists ? "login" : "signup");
      }
    } catch {
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Finish a successful sign-in — shared by the plain and 2FA paths.
  const completeLogin = () => {
    trackLogin("email");
    toast({ title: "Welcome Back", description: "Login successful!" });
    const callbackUrl = searchParams.get("callbackUrl") || "/user/dashboard";
    router.push(callbackUrl);
    router.refresh();
  };

  /**
   * Turn an Auth.js credentials error code into a message.
   *
   * `totp_required` is handled by the caller (it's a step change, not a
   * failure). Everything else that means "wrong credentials" shares one
   * message so this doesn't become an account-enumeration oracle.
   */
  const describeLoginError = (code: string | undefined): string => {
    switch (code) {
      case "account_locked":
        return "Too many failed attempts. Try again in about 15 minutes.";
      case "totp_invalid":
        return "That authenticator code wasn't right. Try the next one.";
      default:
        return "Invalid email or password.";
    }
  };

  // Step 2a: Login with credentials
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const result = await signIn("credentials", {
        email,
        password,
        redirect: false,
      });

      // Password was right but the account has an authenticator enrolled.
      // Move to the code step and re-submit with the same credentials plus
      // the code — the provider re-checks everything in one pass.
      if (result?.code === "totp_required") {
        setTotpCode("");
        setStep("twofa");
        setIsLoading(false);
        return;
      }

      if (result?.error) {
        toast({
          title: "Login Failed",
          description: describeLoginError(result.code),
          variant: "destructive",
        });
        setIsLoading(false);
        return;
      }

      completeLogin();
    } catch {
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
      setIsLoading(false);
    }
  };

  // Step 2a-ii: Second factor for accounts with an authenticator enrolled
  const handleTwoFactor = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const result = await signIn("credentials", {
        email,
        password,
        totp: totpCode,
        redirect: false,
      });

      if (result?.error) {
        toast({
          title: "Verification Failed",
          description: describeLoginError(result.code),
          variant: "destructive",
        });
        setTotpCode("");
        // A lockout invalidates the password too, so send them back to the
        // start rather than leaving them staring at a code box.
        if (result.code === "account_locked") setStep("login");
        setIsLoading(false);
        return;
      }

      completeLogin();
    } catch {
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
      setIsLoading(false);
    }
  };

  // Step 2b: Register then send OTP
  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) {
      toast({
        title: "Password Too Short",
        description: "Password must be at least 8 characters long.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const res = await fetch("/api/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, password }),
      });

      const data = await res.json();

      if (!res.ok) {
        if (res.status === 409) {
          // Account already exists — likely unverified, resend OTP
          try {
            await sendOtp(email);
          } catch (err) {
            toast({
              title: "Could not send code",
              description: err instanceof Error ? err.message : "Please try again.",
              variant: "destructive",
            });
            setIsLoading(false);
            return;
          }
          setCooldown(60);
          setOtpValue("");
          setOtpError("");
          setStep("otp");
          setIsLoading(false);
          return;
        }
        toast({
          title: "Registration Failed",
          description: data.error || "Could not create account.",
          variant: "destructive",
        });
        setIsLoading(false);
        return;
      }

      trackSignUp("email");

      // Meta CompleteRegistration (browser Pixel). Uses the server-minted id from
      // the register response so it deduplicates against the CAPI event that
      // carries the same event_id. Only fires on a real create (409/failed paths
      // returned above).
      if (data.registrationEventId) {
        trackCompleteRegistration(data.registrationEventId);
      }

      // Send OTP for email verification. If this fails, the user row was
      // already created — still surface the failure so they know to retry
      // via the resend button instead of waiting for an email that never
      // arrives.
      try {
        await sendOtp(email);
      } catch (err) {
        toast({
          title: "Account created, but we could not send the code",
          description:
            (err instanceof Error ? err.message : "Please try again.") +
            " You can request a new code on the next screen.",
          variant: "destructive",
        });
      }

      setCooldown(60);
      setOtpValue("");
      setOtpError("");
      setStep("otp");
    } catch {
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Step 3: Verify OTP then auto-login
  const handleVerifyOtp = async () => {
    if (otpValue.length !== 6) return;
    setIsLoading(true);
    setOtpError("");

    try {
      const res = await fetch("/api/auth/verify-email-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, otp: otpValue }),
      });

      const data = await res.json();

      if (!res.ok) {
        setOtpError(data.error || "Verification failed. Please try again.");
        setIsLoading(false);
        return;
      }

      // Auto-login after verification
      const signInResult = await signIn("credentials", {
        email,
        password,
        redirect: false,
      });

      if (signInResult?.error) {
        toast({
          title: "Account Verified",
          description: "Please sign in to continue.",
        });
        setStep("login");
        setPassword("");
        setIsLoading(false);
        return;
      }

      trackEvent("email_verified", { method: "otp" });
      router.push("/user/onboarding");
    } catch {
      setOtpError("Verification failed. Please try again.");
      setIsLoading(false);
    }
  };

  // Resend OTP
  const handleResendOtp = async () => {
    if (cooldown > 0 || otpSending) return;
    setOtpSending(true);
    setOtpError("");

    try {
      await sendOtp(email);
      setCooldown(60);
      setOtpValue("");
      toast({ title: "OTP Sent", description: "A new code has been sent to your email." });
    } catch (err) {
      setOtpError(
        err instanceof Error ? err.message : "Failed to resend. Please try again."
      );
    } finally {
      setOtpSending(false);
    }
  };

  // Forgot password
  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const res = await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      toast({
        title: "Check Your Email",
        description: data.message || "If an account exists, a reset link has been sent.",
      });
      setStep("login");
    } catch {
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleAuth = async () => {
    setIsLoading(true);
    trackLogin("google");
    try {
      await signIn("google", {
        callbackUrl: searchParams.get("callbackUrl") || "/user/dashboard",
      });
    } catch {
      toast({
        title: "Error",
        description: "Could not connect to Google. Please try again.",
        variant: "destructive",
      });
      setIsLoading(false);
    }
  };

  const goBack = () => {
    setPassword("");
    setName("");
    setAgreedToTerms(false);
    setOtpValue("");
    setOtpError("");
    setStep("email");
  };

  // --- Titles & subtitles per step ---
  const titles: Record<AuthStep, string> = {
    email: "Welcome to Hoku",
    login: "Well, Hello Again.",
    signup: "Let's Make It Official.",
    otp: "Almost There!",
    forgot: "Reset Password",
    twofa: "One More Step",
  };

  const subtitles: Record<AuthStep, string> = {
    email: "Your AI wardrobe assistant awaits.",
    login: "I was about to send a search party into your closet.",
    signup: "I can't start saving your wardrobe until I know your name. The cosmos has rules.",
    otp: `We sent a 6-digit code to ${email}`,
    forgot: "Enter your email and we'll send you a reset link.",
    twofa: "Enter the 6-digit code from your authenticator app.",
  };

  const inputClasses = "rounded-xl border-2 border-[#4c4c4c] focus:border-[#4c4c4c] focus:ring-2 focus:ring-[#ffd372] font-winky bg-white shadow-[2px_2px_0_#4c4c4c] py-3";
  const buttonClasses = "w-full bg-[#ffd372] text-charcoal font-winky font-semibold py-3 rounded-lg transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 shadow-[3px_3px_0_#4c4c4c] border-2 border-[#4c4c4c]";

  return (
    <div className="flex items-center justify-center min-h-screen bg-cream px-6 py-8">
      <Card className="w-full max-w-md rounded-3xl border-2 border-[#4c4c4c] bg-[#fffdfa] shadow-[6px_6px_0_#4c4c4c]">
        <CardHeader className="pb-4 pt-7 text-center">
          <Link
            href="/"
            className="mx-auto mb-3 inline-flex items-center justify-center"
            aria-label="Hoku home"
          >
            <span className="flex h-14 w-14 items-center justify-center rounded-2xl border-2 border-[#4c4c4c] bg-white shadow-[3px_3px_0_#4c4c4c]">
              <img src="/logo.png" alt="Hoku" className="h-9 w-9 object-contain" />
            </span>
          </Link>
          <CardTitle className="font-winky text-2xl font-bold text-charcoal">
            {titles[step]}
          </CardTitle>
          <CardDescription className="font-winky text-sm text-charcoal/70">
            {subtitles[step]}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* ---- STEP: EMAIL ---- */}
          {step === "email" && (
            <>
              {/* Primary: Google */}
              <Button
                onClick={handleGoogleAuth}
                className={buttonClasses}
                disabled={isLoading}
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC04" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                Continue with Google
              </Button>

              <div className="flex items-center gap-3">
                <div className="h-px flex-1 bg-[#4c4c4c]/20" />
                <span className="font-winky text-[10px] font-bold uppercase tracking-wider text-charcoal/50">
                  Or with email
                </span>
                <div className="h-px flex-1 bg-[#4c4c4c]/20" />
              </div>

              {/* Secondary: Email */}
              <form onSubmit={handleEmailContinue} className="space-y-4">
                <div className="space-y-2">
                  <label className="font-winky text-sm font-medium text-charcoal">
                    Email
                  </label>
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={inputClasses}
                    required
                    disabled={isLoading}
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-white border-2 border-[#4c4c4c] text-charcoal font-winky font-semibold py-3 rounded-lg transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#fffdfa] active:translate-y-0 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 shadow-[2px_2px_0_#4c4c4c]"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center"><Spinner />Checking...</span>
                  ) : (
                    "Continue with Email"
                  )}
                </Button>
              </form>

              <p className="text-center font-patrick text-[11px] leading-relaxed text-charcoal/50">
                By continuing you agree to our{" "}
                <Link href="/legal/terms" target="_blank" className="underline hover:text-charcoal">
                  terms
                </Link>{" "}
                and{" "}
                <Link href="/legal/privacy" target="_blank" className="underline hover:text-charcoal">
                  privacy policy
                </Link>
                .
              </p>
            </>
          )}

          {/* ---- STEP: LOGIN ---- */}
          {step === "login" && (
            <>
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="flex items-center gap-2">
                  <p className="font-winky text-sm text-charcoal/70">{email}</p>
                  <button
                    type="button"
                    onClick={goBack}
                    className="font-winky text-xs text-charcoal/50 hover:text-charcoal underline"
                  >
                    Change
                  </button>
                </div>
                <div className="space-y-2">
                  <label className="font-winky text-sm font-medium text-charcoal">
                    Password
                  </label>
                  <Input
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={inputClasses}
                    required
                    disabled={isLoading}
                    minLength={8}
                    autoFocus
                  />
                </div>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => setStep("forgot")}
                    className="text-sm font-winky text-charcoal/80 hover:underline"
                  >
                    Forgot Password?
                  </button>
                </div>
                <Button type="submit" className={buttonClasses} disabled={isLoading}>
                  {isLoading ? (
                    <span className="flex items-center justify-center"><Spinner />Signing in...</span>
                  ) : (
                    "Sign In"
                  )}
                </Button>
              </form>

              <p className="text-center text-sm font-winky text-charcoal/60">
                <button type="button" onClick={goBack} className="text-[#4c4c4c] font-semibold hover:underline">
                  Back
                </button>
              </p>
            </>
          )}

          {/* ---- STEP: SIGNUP ---- */}
          {step === "signup" && (
            <>
              <form onSubmit={handleSignup} className="space-y-4">
                <div className="flex items-center gap-2">
                  <p className="font-winky text-sm text-charcoal/70">{email}</p>
                  <button
                    type="button"
                    onClick={goBack}
                    className="font-winky text-xs text-charcoal/50 hover:text-charcoal underline"
                  >
                    Change
                  </button>
                </div>
                <div className="space-y-2">
                  <label className="font-winky text-sm font-medium text-charcoal">
                    Name
                  </label>
                  <Input
                    type="text"
                    placeholder="Your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className={inputClasses}
                    required
                    disabled={isLoading}
                    autoFocus
                  />
                </div>
                <div className="space-y-2">
                  <label className="font-winky text-sm font-medium text-charcoal">
                    Password
                  </label>
                  <Input
                    type="password"
                    placeholder="Min. 8 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={inputClasses}
                    required
                    disabled={isLoading}
                    minLength={8}
                  />
                </div>

                <div className="flex items-start space-x-3 pt-2">
                  <Checkbox
                    id="terms"
                    checked={agreedToTerms}
                    onCheckedChange={(checked) => setAgreedToTerms(checked as boolean)}
                    className="mt-0.5"
                  />
                  <label
                    htmlFor="terms"
                    className="text-sm font-winky text-charcoal/80 leading-relaxed"
                  >
                    I agree to the{" "}
                    <Link href="/legal/terms" target="_blank" className="text-[#4c4c4c] hover:underline font-semibold">
                      terms & conditions
                    </Link>
                    ,{" "}
                    <Link href="/legal/privacy" target="_blank" className="text-[#4c4c4c] hover:underline font-semibold">
                      privacy policy
                    </Link>
                    , and{" "}
                    <Link href="/legal/cookies" target="_blank" className="text-[#4c4c4c] hover:underline font-semibold">
                      cookie policy
                    </Link>
                    .
                  </label>
                </div>

                <Button
                  type="submit"
                  className={buttonClasses}
                  disabled={isLoading || !agreedToTerms}
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center"><Spinner />Creating...</span>
                  ) : (
                    "Create Account"
                  )}
                </Button>
              </form>

              <p className="text-center text-sm font-winky text-charcoal/60">
                <button type="button" onClick={goBack} className="text-[#4c4c4c] font-semibold hover:underline">
                  Back
                </button>
              </p>
            </>
          )}

          {/* ---- STEP: OTP ---- */}
          {step === "otp" && (
            <div className="space-y-6">
              <div className="flex justify-center">
                <InputOTP maxLength={6} value={otpValue} onChange={setOtpValue} autoFocus>
                  <InputOTPGroup>
                    <InputOTPSlot index={0} className="h-12 w-12 border-[#ffd372] bg-cream font-winky text-lg" />
                    <InputOTPSlot index={1} className="h-12 w-12 border-[#ffd372] bg-cream font-winky text-lg" />
                    <InputOTPSlot index={2} className="h-12 w-12 border-[#ffd372] bg-cream font-winky text-lg" />
                    <InputOTPSlot index={3} className="h-12 w-12 border-[#ffd372] bg-cream font-winky text-lg" />
                    <InputOTPSlot index={4} className="h-12 w-12 border-[#ffd372] bg-cream font-winky text-lg" />
                    <InputOTPSlot index={5} className="h-12 w-12 border-[#ffd372] bg-cream font-winky text-lg" />
                  </InputOTPGroup>
                </InputOTP>
              </div>

              {otpError && (
                <p className="font-winky text-sm text-red-500 text-center">{otpError}</p>
              )}

              <Button
                onClick={handleVerifyOtp}
                className={buttonClasses}
                disabled={otpValue.length !== 6 || isLoading}
              >
                {isLoading ? (
                  <span className="flex items-center justify-center"><Spinner />Verifying...</span>
                ) : (
                  "Verify"
                )}
              </Button>

              <p className="text-center">
                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={cooldown > 0 || otpSending}
                  className="font-winky text-sm text-charcoal/60 underline disabled:opacity-50 disabled:no-underline"
                >
                  {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend Code"}
                </button>
              </p>
            </div>
          )}

          {/* ---- STEP: TWO-FACTOR (authenticator app) ---- */}
          {step === "twofa" && (
            <form onSubmit={handleTwoFactor} className="space-y-6">
              <div className="flex justify-center">
                <InputOTP
                  maxLength={6}
                  value={totpCode}
                  onChange={setTotpCode}
                  autoFocus
                >
                  <InputOTPGroup>
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <InputOTPSlot
                        key={i}
                        index={i}
                        className="h-12 w-12 border-[#ffd372] bg-cream font-winky text-lg"
                      />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>

              <Button
                type="submit"
                className={buttonClasses}
                disabled={totpCode.length !== 6 || isLoading}
              >
                {isLoading ? (
                  <span className="flex items-center justify-center">
                    <Spinner />
                    Verifying...
                  </span>
                ) : (
                  "Verify"
                )}
              </Button>

              <p className="text-center">
                <button
                  type="button"
                  onClick={() => {
                    setTotpCode("");
                    setStep("login");
                  }}
                  className="font-winky text-sm text-charcoal/60 underline"
                >
                  Back
                </button>
              </p>
            </form>
          )}

          {/* ---- STEP: FORGOT PASSWORD ---- */}
          {step === "forgot" && (
            <>
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div className="space-y-2">
                  <label className="font-winky text-sm font-medium text-charcoal">
                    Email
                  </label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={inputClasses}
                    required
                    disabled={isLoading}
                    autoFocus
                  />
                </div>
                <Button type="submit" className={buttonClasses} disabled={isLoading}>
                  {isLoading ? (
                    <span className="flex items-center justify-center"><Spinner />Sending...</span>
                  ) : (
                    "Send Reset Link"
                  )}
                </Button>
              </form>
              <p className="text-center text-sm font-winky text-charcoal/60">
                <button type="button" onClick={() => setStep("login")} className="text-[#4c4c4c] font-semibold hover:underline">
                  Back to Sign In
                </button>
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
